/**
 * Active Workspace Component
 * Phase 7: Workspace Surface Layer - Main Content Area
 * 
 * Renders the active workspace content based on selected page.
 * Dynamically loads appropriate surface (HomePage, PageEditor, DatabaseView, etc.)
 */

import React, { useMemo, Suspense } from 'react';
import { Box, Center, Loader, Stack, Text, useMantineColorScheme } from '@mantine/core';
import { Card } from '../../primitives/Card';
import { IconHome } from '@tabler/icons-react';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import { loadDatabase, saveDatabase } from '../../../core/workspace/databaseStorage';
import { eventBus } from '../../../core/event-bus';
import type { DatabaseItem } from '../../../types/models/Database';
import HomePage from './HomePage';
import PageEditor from './PageEditor';
import DatabaseView from './DatabaseView';

/**
 * Active Workspace Props
 */
interface ActiveWorkspaceProps {
  /** Custom className */
  className?: string;
  /** Custom style */
  style?: React.CSSProperties;
}

/**
 * Loading Fallback Component
 */
const WorkspaceLoading: React.FC = () => (
  <Center h="100%" w="100%">
    <Stack align="center" gap="md">
      <Loader size="lg" />
      <Text c="dimmed" size="sm">Loading workspace...</Text>
    </Stack>
  </Center>
);

/**
 * Home View - Notion-style gallery with page cards
 */
const HomeView: React.FC = () => {
  const { pages, selectPage, createPage } = useWorkspace();
  const { colorScheme } = useMantineColorScheme();
  
  // Dark mode colors
  const bgPrimary = colorScheme === 'dark' ? '#1f1f1f' : '#ffffff';
  const bgHover = colorScheme === 'dark' ? '#2d2d2d' : '#f7f6f3';
  const textPrimary = colorScheme === 'dark' ? '#e8eaed' : '#37352f';
  const textMuted = colorScheme === 'dark' ? '#9aa0a6' : '#8e8e8e';
  const borderColor = colorScheme === 'dark' ? '#3c4043' : '#e9e9e7';

  const handleCreatePage = async (template?: string) => {
    const titles: Record<string, string> = {
      'blank': 'Untitled',
      'meeting': 'Meeting Notes',
      'task': 'Task List',
      'doc': 'Documentation',
      'database': 'Untitled Database',
    };
    const types: Record<string, 'page' | 'database'> = {
      'blank': 'page',
      'meeting': 'page',
      'task': 'page',
      'doc': 'page',
      'database': 'database',
    };
    try {
      const newPage = await createPage(
        titles[template || 'blank'] || 'Untitled',
        undefined,
        types[template || 'blank'] || 'page'
      );
      await selectPage(newPage.id);
    } catch (e) {
      console.error('Failed to create page:', e);
    }
  };

  /**
   * Seed a "Quick Notes" database with sample kanban items.
   * Re-uses an existing "Quick Notes" page if one already exists, otherwise creates a new one.
   * Then writes 4 sample items into the underlying database and navigates to it.
   */
  const handleSeedDemo = async () => {
    try {
      const existing = pages.find(p => p.title === 'Quick Notes');
      let pageId: string;
      if (existing) {
        pageId = existing.id;
        await selectPage(pageId);
      } else {
        const newPage = await createPage('Quick Notes', undefined, 'database');
        pageId = newPage.id;
      }
      // The page id doubles as the database id (see WorkspaceContext.createPage).
      const db = loadDatabase(pageId);
      if (!db) {
        console.error('[SeedDemo] No database found for pageId', pageId);
        return;
      }
      // Don't double-seed — bail if items already present.
      if (db.items.length > 0) {
        await selectPage(pageId);
        return;
      }
      const now = new Date();
      const mkItem = (id: string, title: string, status: 'active' | 'completed', tag: string): DatabaseItem => ({
        id,
        databaseId: pageId,
        title,
        properties: [
          { propertyId: 'title', value: title },
          { propertyId: 'date', value: now.toISOString() },
          {
            propertyId: 'status',
            value: { id: status === 'active' ? 'status_active' : 'status_completed', name: status === 'active' ? 'Active' : 'Completed', color: status === 'active' ? '#52b788' : '#adb5bd', state: status },
          },
          { propertyId: 'tag', value: [tag] },
          { propertyId: 'finished', value: status === 'completed' },
        ],
        createdAt: now,
        updatedAt: now,
      });
      db.items = [
        mkItem(`${pageId}_i1`, 'Ship kanban drag-and-drop', 'completed', 'FRESHVIBE'),
        mkItem(`${pageId}_i2`, 'Add multi-select tag filter', 'active', 'FRESHVIBE'),
        mkItem(`${pageId}_i3`, 'Wire up status property to board columns', 'active', 'FRESHVIBE'),
        mkItem(`${pageId}_i4`, 'Write seed-data dev shortcut', 'completed', 'FRESHVIBE'),
      ];
      saveDatabase(db);
      // Tell the mounted DatabaseView to re-load from storage so it picks up
      // the freshly-seeded items (it only reads from storage on mount).
      eventBus.emit('database:refresh', { databaseId: pageId });
      await selectPage(pageId);
    } catch (e) {
      console.error('[SeedDemo] Failed:', e);
    }
  };

  const templateCards = [
    { id: 'blank', emoji: '📄', title: 'Blank Page', desc: 'Start with a clean slate' },
    { id: 'meeting', emoji: '📅', title: 'Meeting Notes', desc: 'Capture meeting outcomes' },
    { id: 'task', emoji: '✅', title: 'Task List', desc: 'Track your tasks and to-dos' },
    { id: 'doc', emoji: '📝', title: 'Documentation', desc: 'Write technical docs' },
    { id: 'database', emoji: '🗂', title: 'New Database', desc: 'Kanban / table / list view' },
    { id: 'seed', emoji: '🚀', title: 'Seed demo data', desc: 'Populate a "Quick Notes" kanban with sample cards', accent: true },
  ];

  return (
    <Box 
      h="100%" 
      w="100%" 
      style={{ overflow: 'auto', background: bgPrimary }}
    >
      <Box p="xl" style={{ maxWidth: 1000, margin: '0 auto' }}>
        {/* Header */}
        <Box mb="xl">
          <Text size="xl" fw={700} mb={4} style={{ color: textPrimary }}>Home</Text>
          <Text size="sm" c="dimmed">Welcome to your workspace</Text>
        </Box>

        {/* Quick Actions - Templates */}
        <Box mb="xl">
          <Text fw={600} mb="md" size="sm" c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Quick Create
          </Text>
          <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            {templateCards.map(template => {
              const isSeed = template.id === 'seed';
              return (
              <Card
                key={template.id}
                interactive
                onClick={() => isSeed ? handleSeedDemo() : handleCreatePage(template.id)}
                style={isSeed ? {
                  background: 'linear-gradient(135deg, #7c3aed22 0%, #ec489922 100%)',
                  borderColor: '#7c3aed66',
                } : undefined}
              >
                <Text size="xl" mb="xs">{template.emoji}</Text>
                <Text fw={500} size="sm" mb={4} style={{ color: textPrimary }}>{template.title}</Text>
                <Text size="xs" c="dimmed">{template.desc}</Text>
              </Card>
              );
            })}
          </Box>
        </Box>

        {/* All Pages Gallery */}
        {pages.length > 0 && (
          <Box>
            <Text fw={600} mb="md" size="sm" c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              All Pages
            </Text>
            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
                {pages.map(page => (
                  <Card
                    key={page.id}
                    interactive
                    style={{ minHeight: 120, cursor: 'pointer' }}
                    onClick={() => selectPage(page.id)}
                  >
                    <Text fw={500} size="sm" mb="xs" style={{ color: textPrimary }}>{page.title}</Text>
                    <Text size="xs" c="dimmed">
                      {page.type === 'database' ? '📊 Database' : page.type === 'view' ? '👁 View' : '📄 Page'}
                    </Text>
                  </Card>
                ))}
            </Box>
          </Box>
        )}

        {/* Empty State */}
        {pages.length === 0 && (
          <Box 
            p="xl" 
            ta="center" 
            style={{ 
              background: bgHover, 
              borderRadius: 12,
              border: `2px dashed ${borderColor}`
            }}
          >
            <Text size="xl" mb="md" style={{ color: textMuted }}>No pages yet</Text>
            <Text c="dimmed" mb="lg">Create your first page to get started</Text>
            <Box 
              component="button"
              p="sm"
              style={{ 
                background: '#2383e2', 
                color: '#fff', 
                border: 'none', 
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 500
              }}
              onClick={() => handleCreatePage('blank')}
            >
              + Create your first page
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/**
 * Page Editor Wrapper Component
 * Simply renders PageEditor which gets its data from context
 */
const PageEditorWrapper: React.FC = () => {
  return <PageEditor />;
};

/**
 * Database View Wrapper Component
 * Loads the database by pageId (the page id doubles as the database id)
 * and passes the real title to the DatabaseView.
 */
interface DatabaseViewWrapperProps {
  pageId: string;
  title: string;
}

const DatabaseViewWrapper: React.FC<DatabaseViewWrapperProps> = ({ pageId, title }) => {
  return (
    <Suspense fallback={<WorkspaceLoading />}>
      <DatabaseView
        id={pageId}
        phase={7}
        title={title}
        description={`Database: ${title}`}
        isActive={true}
      />
    </Suspense>
  );
};

const ContentRouter: React.FC<ContentRouterProps> = ({ pageType = 'page' }) => {
  const { currentPageId, currentPage } = useWorkspace();
  const component = useMemo(() => {
    switch (pageType) {
      case 'database':
        return <DatabaseViewWrapper pageId={currentPageId ?? ''} title={currentPage?.title ?? 'Database'} />;

      case 'view':
        return <DatabaseViewWrapper pageId={currentPageId ?? ''} title={currentPage?.title ?? 'View'} />;

      case 'page':
      default:
        return <PageEditorWrapper />;
    }
  }, [pageType, currentPageId, currentPage?.title]);

  return (
    <Box h="100%" w="100%" style={{ display: 'flex', flexDirection: 'column' }}>
      {component}
    </Box>
  );
};

/**
 * Active Workspace Component
 * 
 * Main content area that displays the currently selected page's content.
 * Handles navigation between different page types and manages the active surface.
 */
export const ActiveWorkspace: React.FC<ActiveWorkspaceProps> = ({ 
  className = '', 
  style = {} 
}) => {
  const { currentPageId, currentPage, pages, initialized } = useWorkspace();

  // Determine what to render
  const renderContent = useMemo(() => {
    // Still initializing - show loading
    if (!initialized) {
      return (
        <Suspense fallback={<WorkspaceLoading />}>
          <Center h="100%" w="100%">
            <Stack align="center" gap="md">
              <Loader size="lg" />
              <Text c="dimmed" size="sm">Loading workspace...</Text>
            </Stack>
          </Center>
        </Suspense>
      );
    }

    // No page selected - show home view with recent pages
    if (!currentPageId || !currentPage) {
      return (
        <Suspense fallback={<WorkspaceLoading />}>
          <HomeView />
        </Suspense>
      );
    }

    // Page is selected - show appropriate content
    return (
      <ContentRouter
        pageId={currentPageId}
        pageTitle={currentPage.title}
        pageType={currentPage.type}
      />
    );
  }, [initialized, currentPageId, currentPage, pages]);

  return (
    <Box
      component="section"
      className={`active-workspace ${className}`}
      h="100%"
      w="100%"
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: 'var(--mantine-color-gray-0)',
        ...style
      }}
    >
      {renderContent}
    </Box>
  );
};

export default ActiveWorkspace;
